// BeaconHistory.tsx — every ping ever made by the agency's beacons
// (kept 30 days on the backend). Filter by sheet, result, or "only new leads".

import React, { useCallback, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { Clock, Hand, History, Inbox, RefreshCw, Sparkles, Timer, Zap } from 'lucide-react';
import CustomPaginator from '../../../components/GlobalComponents/CustomPaginator';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { TONES, fmtDateTime, pillStyle, shortId, userLabel } from '../../SheetsManager/parts/SheetImportDetailsModal';
import BeaconDot from './BeaconDot';
import { BeaconPing, PageInfo, SheetBeacon, apiError, beaconApi, beaconName, fmtAgo } from './beaconTypes';

const SHELL: React.CSSProperties = { background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' };
const INNER: React.CSSProperties = { background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px' };
const SELECT_ARROW = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='5'%3E%3Cpath d='M0 0l4.5 5L9 0z' fill='rgba(17,85,80,0.45)'/%3E%3C/svg%3E")`;
const EMPTY_PAGE: PageInfo = { total: 0, page: 1, limit: 20, pages: 0 };

const selectStyle = (filled: boolean): React.CSSProperties => ({
  height: '31px',
  minWidth: '140px',
  maxWidth: '220px',
  background: 'white',
  border: `1px solid ${G.line}`,
  borderRadius: '7.4px',
  fontSize: '0.7rem',
  padding: '0 24px 0 9px',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: SELECT_ARROW,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
  color: filled ? C.teal : '#374151',
  outline: 'none',
});

const TRIGGER: Record<string, { label: string; icon: React.ReactNode }> = {
  schedule: { label: 'Scheduled', icon: <Clock size={10} /> },
  manual: { label: 'Ping now', icon: <Hand size={10} /> },
  setup: { label: 'Baseline', icon: <Sparkles size={10} /> },
};

interface BeaconHistoryProps {
  beacons: SheetBeacon[];
  /** '' = all sheets */
  beaconId: string;
  onBeaconIdChange: (id: string) => void;
  /** Bump to reload (e.g. after a ping). */
  refreshKey: number;
  now: number;
}

const BeaconHistory: React.FC<BeaconHistoryProps> = ({ beacons, beaconId, onBeaconIdChange, refreshKey, now }) => {
  const [pings, setPings] = useState<BeaconPing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState<PageInfo>(EMPTY_PAGE);

  const fetchPings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const p = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (beaconId) p.set('beacon_id', beaconId);
      if (status) p.set('status', status);
      if (onlyNew) p.set('only_new', 'true');
      const res = await beaconApi.pings(p);
      setPings(res.data ?? []);
      setPagination(res.pagination ?? EMPTY_PAGE);
    } catch (e) {
      setError(apiError(e, 'Failed to load ping history'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, beaconId, status, onlyNew]);

  useEffect(() => {
    fetchPings();
  }, [fetchPings, refreshKey]);

  // Filter change from the parent (e.g. "History" on a tree leaf) → page 1
  useEffect(() => {
    setPage(1);
  }, [beaconId]);

  const byId = new Map(beacons.map((b) => [b.id, b]));
  const sheetLabel = (p: BeaconPing) => {
    const b = byId.get(p.beacon_id);
    return b ? beaconName(b) : shortId(p.sheet_id);
  };

  // ── Templates ────────────────────────────────────────────────────────────
  const whenTpl = (p: BeaconPing) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ fontSize: '0.72rem', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtDateTime(p.checked_at)}</span>
      <span style={{ fontSize: '0.6rem', color: C.muted }}>{fmtAgo(p.checked_at, now)}</span>
    </div>
  );

  const sheetTpl = (p: BeaconPing) => (
    <span style={{ fontSize: '0.72rem', color: C.teal, fontWeight: 700, whiteSpace: 'nowrap' }}>{sheetLabel(p)}</span>
  );

  const triggerTpl = (p: BeaconPing) => {
    const t = TRIGGER[p.trigger] ?? { label: p.trigger, icon: null };
    return (
      <span style={pillStyle(p.trigger === 'manual' ? TONES.blue : p.trigger === 'setup' ? TONES.lime : TONES.grey)}>
        {t.icon} {t.label}
      </span>
    );
  };

  const resultTpl = (p: BeaconPing) => {
    if (p.status === 'error') {
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', maxWidth: '300px' }}>
          <span style={{ marginTop: '4px', display: 'flex' }}>
            <BeaconDot health="error" size={8} />
          </span>
          <span style={{ fontSize: '0.68rem', color: '#b91c1c', fontWeight: 600, lineHeight: 1.45 }}>{p.error || 'Check failed'}</span>
        </div>
      );
    }
    if (p.new_rows > 0) {
      return (
        <span style={{ ...pillStyle(TONES.lime), fontSize: '0.66rem' }}>
          <Zap size={10} /> +{p.new_rows} new lead{p.new_rows === 1 ? '' : 's'}
        </span>
      );
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.68rem', color: C.sub }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e' }} />
        {p.trigger === 'setup' ? 'Baseline set' : 'No new rows'}
      </span>
    );
  };

  const num = (v: number | string, color = '#374151') => (
    <span style={{ fontSize: '0.72rem', color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
  );

  const emptyState = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '32px 0', textAlign: 'center' }}>
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: '50%',
          background: 'rgba(17,85,80,0.07)',
          border: '1px solid rgba(17,85,80,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Inbox size={21} color={C.teal} />
      </div>
      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: C.teal }}>No pings yet</span>
      <span style={{ fontSize: '0.68rem', color: C.muted }}>Each check a beacon makes shows up here.</span>
    </div>
  );

  const CH: React.CSSProperties = { whiteSpace: 'nowrap' };

  return (
    <div style={SHELL}>
      <style>{`
        .bch-dt .p-datatable-thead > tr > th { background:#fafbfb !important; color:#9ca3af !important; font-size:0.62rem !important; font-weight:700 !important; text-transform:uppercase !important; letter-spacing:0.5px !important; border-bottom:1px solid ${G.soft} !important; padding:9px 12px !important; }
        .bch-dt .p-datatable-tbody > tr > td { border-bottom:1px solid ${G.softer} !important; padding:9px 12px !important; vertical-align: middle; }
        .bch-dt .p-datatable-tbody > tr:last-child > td { border-bottom:none !important; }
        .bch-dt .p-datatable-tbody > tr.bch-new { background: rgba(200,233,75,0.08) !important; }
        .bch-dt .p-datatable-tbody > tr.bch-err { background: rgba(220,38,38,0.025) !important; }
        .bch-dt .p-datatable-emptymessage > td { padding:0 !important; text-align:center !important; border-bottom:none !important; }
        .bch-ghost:hover { background: rgba(17,85,80,0.08) !important; color: #115550 !important; }
        .bch-spin { animation: bchSpin 0.9s linear infinite; }
        @keyframes bchSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ ...INNER, overflow: 'hidden' }}>
        {/* Toolbar */}
        <div
          style={{
            padding: '11px 16px',
            borderBottom: `1px solid ${G.soft}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '9px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
            <div style={{ width: 3, height: 14.8, background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: 3 }} />
            <History size={13} color={C.teal} />
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>Beacon History</span>
            <span
              style={{
                padding: '1.8px 8.3px',
                borderRadius: '20px',
                background: 'rgba(17,85,80,0.07)',
                border: '1px solid rgba(17,85,80,0.18)',
                color: C.tealMid,
                fontSize: '0.63rem',
                fontWeight: 600,
              }}
            >
              {pagination.total} pings
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6.5px', flexWrap: 'wrap' }}>
            <select value={beaconId} onChange={(e) => onBeaconIdChange(e.target.value)} style={selectStyle(!!beaconId)} aria-label="Sheet">
              <option value="">All sheets</option>
              {beacons.map((b) => (
                <option key={b.id} value={b.id}>
                  {beaconName(b)}
                </option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              style={{ ...selectStyle(!!status), minWidth: '110px' }}
              aria-label="Result"
            >
              <option value="">All results</option>
              <option value="ok">Worked</option>
              <option value="error">Failed</option>
            </select>
            <button
              type="button"
              onClick={() => {
                setOnlyNew((v) => !v);
                setPage(1);
              }}
              aria-pressed={onlyNew}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                height: '31px',
                padding: '0 11px',
                borderRadius: '7.4px',
                fontSize: '0.68rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: onlyNew ? C.lime : 'white',
                color: onlyNew ? C.teal : C.sub,
                border: onlyNew ? `1px solid ${C.limeBorder}` : `1px solid ${G.line}`,
              }}
            >
              <Zap size={11} /> Only new leads
            </button>
            <button
              type="button"
              onClick={fetchPings}
              disabled={loading}
              className="bch-ghost"
              title="Refresh"
              aria-label="Refresh history"
              style={{
                height: 31,
                width: 31,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: G.shellBg,
                border: `1px solid ${G.line}`,
                borderRadius: '7.4px',
                color: C.sub,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <RefreshCw size={12} className={loading ? 'bch-spin' : undefined} />
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '8px 12px 0' }}>
            <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.76rem' }} />
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <DataTable
            value={pings}
            dataKey="id"
            loading={loading}
            emptyMessage={emptyState}
            className="bch-dt"
            rowClassName={(p: BeaconPing) => (p.status === 'error' ? 'bch-err' : p.new_rows > 0 ? 'bch-new' : '')}
            style={{ fontSize: '0.74rem' }}
          >
            <Column header="When" body={whenTpl} headerStyle={{ ...CH, minWidth: '150px' }} />
            <Column header="Sheet" body={sheetTpl} headerStyle={CH} />
            <Column header="Trigger" body={triggerTpl} headerStyle={CH} />
            <Column header="Result" body={resultTpl} headerStyle={{ ...CH, minWidth: '170px' }} />
            <Column header="Rows" body={(p: BeaconPing) => num(p.status === 'ok' ? p.total_rows : '—')} headerStyle={CH} />
            <Column
              header={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Timer size={10} /> Took
                </span>
              }
              body={(p: BeaconPing) => num(p.duration_ms < 1000 ? `${p.duration_ms} ms` : `${(p.duration_ms / 1000).toFixed(1)} s`, C.sub)}
              headerStyle={CH}
            />
            <Column
              header="By"
              body={(p: BeaconPing) => (
                <span style={{ fontSize: '0.68rem', color: C.sub, whiteSpace: 'nowrap' }}>{p.checked_by ? userLabel(p.checked_by) : 'Scheduler'}</span>
              )}
              headerStyle={CH}
            />
          </DataTable>
        </div>

        <CustomPaginator
          page={page}
          totalPages={pagination.pages}
          totalRecords={pagination.total}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          pageSizeOptions={[20, 50, 100]}
          showInfoBar
        />
      </div>
    </div>
  );
};

export default BeaconHistory;
